import { Kelvin, Rankine } from "@/libs/units/temperature";
import type { GasProperties } from "../../fluidProperties/d";
import type { GasPercentages } from "../../productionRates";
import { PSI_BAR_CONVERSION_FACTOR } from "../../constants";
import type { GasState } from "../d";
import { Bar } from "@/libs/units/pressure";
import { CubicFeetPerDay } from "@/libs/units/flowRate";
import {
  getGasContribution,
  getGasPropertiesForComponent
} from "./contributions";
import { solveGasRoot, solveLiquidRoot } from "./pengRobinson";

const toFiniteNumberOrZero = (value: number) => {
  return Number.isFinite(value) ? value : 0;
};

export type GasEntry = {
  gasPercentage: number;
  yM: number;
  yTc: number;
  yPc: number;
};

const GAS_CONSTANT_J_MOL_K = 8.31446;

const GAS_CONSTANT_PSIA_FT3_LBMOL_R = 10.73159;

// A converged grid-search root typically lands with a residual many orders
// of magnitude below this; a residual at/above it means the search's final
// 0.001-resolution window never got close to an actual root — a sign the
// true root sits outside the search interval entirely, rather than that the
// interval is merely coarse.
// const ROOT_RESIDUAL_WARNING_THRESHOLD = 1e-2;
const VOLUME_FACTOR_CONSTANT = 0.0282793;

// function isUnreliableRoot(result: RootSolveResult): boolean {
//   return (
//     result.atSearchBoundary && result.residual > ROOT_RESIDUAL_WARNING_THRESHOLD
//   );
// }
// calculateGasProperties is pure and dominates the cost of a scenario
// evaluation (a Peng-Robinson root search per gas component, at every pump
// stage). Charting and the predictive solvers repeat the exact same inputs
// heavily (~70% of calls), so results are cached on their exact inputs.
// Cached results are frozen because every caller shares them.
const GAS_PROPERTIES_CACHE_SIZE = 4096;
const gasPropertiesCache = new Map<string, readonly GasProperties[]>();

// String(-0) is "0", so keep the sign of zero in the key.
const keyPart = (n: number) => (Object.is(n, -0) ? "-0" : String(n));

// The composition part of the key, remembered per gasPercentages object since
// the same object is passed for every call in an evaluation. Its values are
// kept and compared on use, so an object changed in place still gets the
// right key.
const compositionKeys = new WeakMap<
  GasPercentages,
  { entries: [string, number][]; key: string }
>();
const compositionKey = (gasPercentages: GasPercentages): string => {
  const remembered = compositionKeys.get(gasPercentages);
  if (remembered) {
    let i = 0;
    let unchanged = true;
    for (const component in gasPercentages) {
      const entry = remembered.entries[i++];
      if (
        !entry ||
        entry[0] !== component ||
        !Object.is(entry[1], gasPercentages[component as keyof GasPercentages])
      ) {
        unchanged = false;
        break;
      }
    }
    if (unchanged && i === remembered.entries.length) return remembered.key;
  }
  const entries = Object.entries(gasPercentages);
  let key = "";
  for (const [component, percent] of entries) {
    key += `${component}=${keyPart(percent)};`;
  }
  compositionKeys.set(gasPercentages, { entries, key });
  return key;
};

export function calculateGasProperties(
  gasPercentages: GasPercentages,
  temperatureF: number,
  pressurePSIA: number,
  gasFlowRateSCFD: number,
  bottomTubingPressurePSIA: number
): GasProperties[] {
  const key =
    compositionKey(gasPercentages) +
    keyPart(temperatureF) +
    "|" +
    keyPart(pressurePSIA) +
    "|" +
    keyPart(gasFlowRateSCFD) +
    "|" +
    keyPart(bottomTubingPressurePSIA);

  const cached = gasPropertiesCache.get(key);
  if (cached) {
    // Refresh recency so the Map's insertion order stays least-recently-used first.
    gasPropertiesCache.delete(key);
    gasPropertiesCache.set(key, cached);
    return cached as GasProperties[];
  }

  const result = Object.freeze(
    computeGasProperties(
      gasPercentages,
      temperatureF,
      pressurePSIA,
      gasFlowRateSCFD,
      bottomTubingPressurePSIA
    ).map(entry => (isAbsentEntry(entry) ? entry : Object.freeze(entry)))
  );
  if (gasPropertiesCache.size >= GAS_PROPERTIES_CACHE_SIZE) {
    gasPropertiesCache.delete(gasPropertiesCache.keys().next().value!);
  }
  gasPropertiesCache.set(key, result);
  return result as GasProperties[];
}

// Components absent from the gas (0%) still get a full entry, but nothing
// that depends on their Peng-Robinson roots is calculated until it is read.
// Each of those values is multiplied by the component's zero mole fraction,
// so it is +0 or -0, and every consumer only adds these values into sums
// starting at 0, where adding +/-0 changes nothing. Consumers therefore skip
// absent entries (withoutAbsentGasComponents) and their sums are bit-
// identical, while anything that does read an absent entry (Diagnostics)
// gets exactly the values an eager calculation would have produced. In a
// typical scenario 13 of the 14 components are absent, and their root
// searches were most of the cost.
const ABSENT_COMPONENT = Symbol("absentGasComponent");

// Drops absent components (see ABSENT_COMPONENT) before summing per-component
// values. Only for sums: their values are +/-0, which can't change a sum.
export const withoutAbsentGasComponents = (
  gasProperties: GasProperties[]
): GasProperties[] => gasProperties.filter(gas => !isAbsentEntry(gas));

// Each component's entry has a fixed key order (the table entry's, then the
// pass 1 and pass 2 fields), remembered so lazy entries can match it.
const entryKeyOrder = new Map<
  string,
  { keys: string[]; keySet: Set<string> }
>();
const rememberKeyOrder = (componentKey: string, entry: object) => {
  let order = entryKeyOrder.get(componentKey);
  if (!order) {
    const keys = Object.keys(entry);
    order = { keys, keySet: new Set(keys) };
    entryKeyOrder.set(componentKey, order);
  }
  return order;
};

type AbsentTarget = {
  keys: string[];
  keySet: Set<string>;
  fluidRatio: number;
  calculate: () => GasProperties;
  calculated?: GasProperties;
};

// Lazy absent entries are proxies sharing this handler: much cheaper to make
// than objects with accessor properties, while showing the same keys, in the
// same order, with the same values as the eager entry. Like the frozen eager
// entries they can't be changed. (They can't be structured-cloned; nothing
// posts gas properties to a worker or IndexedDB.)
const absentEntryHandler: ProxyHandler<AbsentTarget> = {
  get(target, key) {
    if (key === ABSENT_COMPONENT) return true;
    if (key === "fluidRatio") return target.fluidRatio;
    if (typeof key !== "string" || !target.keySet.has(key)) return undefined;
    target.calculated ??= target.calculate();
    return target.calculated[key as keyof GasProperties];
  },
  has: (target, key) =>
    key === ABSENT_COMPONENT ||
    (typeof key === "string" && target.keySet.has(key)),
  ownKeys: target => target.keys,
  getOwnPropertyDescriptor(target, key) {
    if (typeof key !== "string" || !target.keySet.has(key)) return undefined;
    return {
      value: absentEntryHandler.get!(target, key, target),
      writable: false,
      enumerable: true,
      // Proxy rules forbid reporting non-configurable properties the target
      // doesn't have.
      configurable: true
    };
  },
  set: () => false,
  defineProperty: () => false,
  deleteProperty: () => false
};

const lazyAbsentEntry = (
  componentKey: string,
  fluidRatio: number,
  calculate: () => GasProperties
): GasProperties => {
  const target: AbsentTarget = {
    keys: [],
    keySet: new Set(),
    fluidRatio,
    calculate
  };
  const known = entryKeyOrder.get(componentKey);
  // The first time a component is seen absent, calculate it once to learn
  // its key order.
  const order =
    known ?? rememberKeyOrder(componentKey, (target.calculated = calculate()));
  target.keys = order.keys;
  target.keySet = order.keySet;
  return new Proxy(target, absentEntryHandler) as unknown as GasProperties;
};

const isAbsentEntry = (entry: GasProperties) =>
  !!(entry as { [ABSENT_COMPONENT]?: true })[ABSENT_COMPONENT];

// lazyAbsentComponents is only turned off by tests, to compare against the
// fully eager calculation.
export function computeGasProperties(
  gasPercentages: GasPercentages,
  temperatureF: number,
  pressurePSIA: number,
  gasFlowRateSCFD: number,
  bottomTubingPressurePSIA: number,
  lazyAbsentComponents = true
): GasProperties[] {
  const kelvinTemperature = Kelvin.toThisUnit(temperatureF);
  const rankineTemperature = Rankine.toThisUnit(temperatureF);
  const barPressure = Bar.toThisUnit(pressurePSIA);

  const components = Object.entries(gasPercentages).map(([key, value]) => {
    const entryGasProperties = getGasPropertiesForComponent(key);
    const contribution = getGasContribution(entryGasProperties, value);
    return {
      key,
      value,
      entryGasProperties,
      contribution,
      absent: lazyAbsentComponents && contribution.gasPercentage === 0
    };
  });

  // Pass 1: compute per-component properties that don't need cross-component aggregates
  const pass1For = (component: (typeof components)[number]): GasProperties => {
    const pass1 = calculateGasEntryPass1(
      component.value,
      component.entryGasProperties,
      component.contribution,
      bottomTubingPressurePSIA,
      pressurePSIA,
      gasFlowRateSCFD,
      rankineTemperature,
      kelvinTemperature,
      barPressure
    );
    return { ...component.entryGasProperties, ...pass1 };
  };
  const pass1Results = components.map(component =>
    component.absent ? undefined : pass1For(component)
  );
  const present = pass1Results.filter(
    (gas): gas is GasProperties => gas !== undefined
  );

  // Aggregates across components. Absent components' terms are +/-0 (see
  // ABSENT_COMPONENT), so leaving them out gives the same sums.
  // Aggregate: sum of columnJ across all components (needed for yv)
  const sumColumnJ = present.reduce((sum, g) => sum + g.columnJ, 0);
  const sumCondiRateBbld = present.reduce((sum, g) => sum + g.condiRateBbld, 0);
  const sumGasRateCfd = present.reduce((sum, g) => sum + g.gasRateCfd, 0);

  // Pass 2: compute remaining properties using cross-component aggregates
  const pass2 = (gas: GasProperties) =>
    calculateGasEntryPass2(
      gas,
      sumColumnJ,
      sumCondiRateBbld,
      sumGasRateCfd,
      pressurePSIA,
      rankineTemperature
    );
  return components.map((component, i) => {
    const gas = pass1Results[i];
    if (gas) {
      const entry = pass2(gas);
      rememberKeyOrder(component.key, entry);
      return entry;
    }
    return lazyAbsentEntry(
      component.key,
      component.contribution.gasPercentage,
      () => pass2(pass1For(component))
    );
  });
}

function getGasState(
  aboveCriticalTemp: boolean,
  aboveCriticalPressure: boolean,
  aboveVaporPressure: boolean
): GasState {
  if (aboveCriticalTemp && !aboveCriticalPressure) return "Gaseous";
  if (!aboveCriticalTemp && aboveVaporPressure) return "Liquid";
  if (!aboveCriticalTemp && !aboveVaporPressure) return "Vapor";
  return "Supercritical";
}

// Pass 1: per-component properties that don't require cross-component aggregates
function calculateGasEntryPass1(
  gasPercentage: number,
  gas: GasProperties,
  contribution: GasEntry,
  tubingPressurePSIA: number,
  pressurePSIA: number,
  gasFlowRateSCFD: number,
  rankineTemperature: number,
  kelvinTemperature: number,
  barPressure: number
): Partial<GasProperties> {
  const fluidRatio = contribution.gasPercentage;
  const effectiveTubingPressurePSIA =
    tubingPressurePSIA > 0 ? tubingPressurePSIA : pressurePSIA;
  const aboveTc = rankineTemperature > gas.criticalTemp;
  const abovePc = effectiveTubingPressurePSIA > gas.criticalPressure;
  const vaporPressurePsi =
    10 ** (gas.antoineA - gas.antoineB / (kelvinTemperature + gas.antoineC)) /
    PSI_BAR_CONVERSION_FACTOR;
  const abovePv = effectiveTubingPressurePSIA > vaporPressurePsi;
  const gasState = getGasState(aboveTc, abovePc, abovePv);
  // PVT!AB78/AC78/AF78/AG78 — the workbook deviates from the textbook
  // Peng-Robinson EOS in three places. They are reproduced verbatim so results
  // tie out to the reference sheet; they are NOT typos introduced by this port:
  //   1. AB78 weights the acentric factor by mole fraction (`=C78*AA78`).
  //      Textbook PR uses the unweighted pure-component acentric factor.
  //   2. AC78 is `=0.37464*1.54226*yw-0.26992*yw^2`. Textbook kappa is
  //      `0.37464 + 1.54226*w - 0.26992*w^2` (a `+` appears as a `*`).
  //   3. AF78/AG78 compute `alpha = m*(1-sqrt(Tr))` and then `a = 0.45724 *
  //      (R^2*Tc^2/Pc) * alpha^2`. Textbook alpha is `[1 + m*(1-sqrt(Tr))]^2`,
  //      i.e. the leading `1 +` is absent here.
  // Net effect: this returns Z slightly above 1 for typical separator
  // conditions where a textbook PR solve returns Z below 1, which is what the
  // gas root search interval below is built around.
  const yw = fluidRatio * gas.acentricFactor;
  const m = 0.37464 * 1.54226 * yw - 0.26992 * yw ** 2;
  // PVT!AD78 — criticalTemp is Rankine; convert to Kelvin for the EOS terms.
  const criticalTempK = Kelvin.toThisUnit(
    Rankine.toStandardUnit(gas.criticalTemp)
  );
  const criticalPressureBar = Bar.toThisUnit(gas.criticalPressure);
  const alpha = m * (1 - Math.sqrt(kelvinTemperature / criticalTempK));
  const a =
    0.45724 *
    ((GAS_CONSTANT_J_MOL_K ** 2 * criticalTempK ** 2) / criticalPressureBar) *
    alpha ** 2;
  const b =
    (0.0778 * (GAS_CONSTANT_J_MOL_K * criticalTempK)) / criticalPressureBar;
  const A =
    (a * barPressure) / (GAS_CONSTANT_J_MOL_K ** 2 * kelvinTemperature ** 2);
  const B = (b * barPressure) / (GAS_CONSTANT_J_MOL_K * kelvinTemperature);
  // PVT!AL78/M433 — gas root, computed for every component.
  const gasRootResult = solveGasRoot(A, B);
  // PVT!AK78/H433 — liquid root, also computed for every component.
  const liquidRootResult = solveLiquidRoot(A, B);
  const zGas = gasRootResult.z;
  const zLiquid = liquidRootResult.z;
  // The search interval matches the reference workbook's own (PVT!M366:
  // M430, B364:B370); when a component's true root falls outside it,
  // the workbook has the same blind spot, but this port additionally
  // flags it rather than silently returning the boundary Z.
  // const gasRootUnreliable = isUnreliableRoot(gasRootResult);
  // const liquidRootUnreliable = isUnreliableRoot(liquidRootResult);
  // PVT!AM78 — volume factor Bg/Bc: uses Z Liquid for Liquid-state
  // components, Z gas for everything else.
  const volumeFactor =
    pressurePSIA > 0
      ? (VOLUME_FACTOR_CONSTANT *
          (gasState === "Liquid" ? zLiquid : zGas) *
          rankineTemperature) /
        pressurePSIA
      : 0;
  const surfaceRateCfd = gasFlowRateSCFD * fluidRatio;
  const inSituRateCfd = surfaceRateCfd * volumeFactor;
  const condiScf = gasState === "Liquid" ? surfaceRateCfd : 0;
  const condiStbd =
    gasState === "Liquid"
      ? CubicFeetPerDay.toStandardUnit(surfaceRateCfd * zLiquid)
      : 0;
  const condiBbld =
    gasState === "Liquid" ? CubicFeetPerDay.toStandardUnit(inSituRateCfd) : 0;
  const gasRateCfd = gasState === "Liquid" ? 0 : inSituRateCfd;
  return {
    fluidRatio,
    yM: contribution.yM,
    vaporPressurePsi,
    columnJ: aboveTc ? 0 : fluidRatio,
    yTc: contribution.yTc,
    yPc: contribution.yPc,
    aboveTc,
    abovePc,
    abovePv,
    gasState,
    yw,
    m,
    alpha,
    a,
    b,
    A,
    B,
    zGas,
    zLiquid,
    volumeFactorBgBc: toFiniteNumberOrZero(volumeFactor),
    surfaceRateCfd: toFiniteNumberOrZero(surfaceRateCfd),
    inSituRateCfd: toFiniteNumberOrZero(inSituRateCfd),
    condiScf: toFiniteNumberOrZero(condiScf),
    condiRateStb: toFiniteNumberOrZero(condiStbd),
    condiRateBbld: toFiniteNumberOrZero(condiBbld),
    gasRateCfd: toFiniteNumberOrZero(gasRateCfd)
  };
}

// Pass 2: properties that require cross-component aggregates (e.g. sumColumnJ for yv)
function calculateGasEntryPass2(
  gas: GasProperties,
  sumColumnJ: number,
  sumCondiRateBbld: number,
  sumGasRateCfd: number,
  pressurePSIA: number,
  rankineTemperature: number
): GasProperties {
  const yv = sumColumnJ > 0 ? gas.columnJ / sumColumnJ : 0;
  const yPv = gas.aboveTc ? 0 : gas.vaporPressurePsi * yv;
  const dewPoint = yPv > 0 ? yv / gas.vaporPressurePsi : 0;
  const yCondi =
    sumCondiRateBbld > 0 ? gas.condiRateBbld / sumCondiRateBbld : 0;
  const yGas = sumGasRateCfd > 0 ? gas.gasRateCfd / sumGasRateCfd : 0;
  const condiYm = yCondi * gas.molarMass;
  const ymGas = yGas * gas.molarMass;
  const gasDensityLbFt3 =
    gas.gasState === "Liquid"
      ? 0
      : gas.zGas > 0
        ? (pressurePSIA * ymGas) /
          (GAS_CONSTANT_PSIA_FT3_LBMOL_R * rankineTemperature * gas.zGas)
        : 0;
  const condensateDensity =
    gas.gasState === "Liquid"
      ? gas.zLiquid > 0
        ? (pressurePSIA * condiYm) /
          (GAS_CONSTANT_PSIA_FT3_LBMOL_R * rankineTemperature * gas.zLiquid)
        : 0
      : 0;
  const condensateSTp = yCondi * gas.liquidStockTankDensity;
  const condensateZ = yCondi * gas.zLiquid;
  const gasZ = yGas * gas.zGas;
  const yCondensateDensity = yCondi * condensateDensity;
  return {
    ...gas,
    yv: toFiniteNumberOrZero(yv),
    yPv: toFiniteNumberOrZero(yPv),
    dewPoint: toFiniteNumberOrZero(dewPoint),
    yCondi: toFiniteNumberOrZero(yCondi),
    yGas: toFiniteNumberOrZero(yGas),
    condiYm: toFiniteNumberOrZero(condiYm),
    ymGas: toFiniteNumberOrZero(ymGas),
    gasDensityLbFt3: toFiniteNumberOrZero(gasDensityLbFt3),
    condensateDensity: toFiniteNumberOrZero(condensateDensity),
    condensateSTp: toFiniteNumberOrZero(condensateSTp),
    condensateZ: toFiniteNumberOrZero(condensateZ),
    gasZ: toFiniteNumberOrZero(gasZ),
    yCondensateDensity: toFiniteNumberOrZero(yCondensateDensity)
  };
}
